import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../utils/api";

export type UnitsLedgerEmployee = {
  emp_name: string;
  emp_id: number;
  role: string;
  shift: string;
  total_units: number;
  design_units_live: number;
  qc_units_live: number;
  design_units_redesign: number;
  qc_units_redesign: number;
  design_units_internal_live: number;
  qc_units_internal_live: number;
  design_units_internal_redesign: number;
  qc_units_internal_redesign: number;
  files_units: number;
  model_units: number;
  log_count: number;
};

export type UnitsLedgerLog = {
  id: number;
  case_id: string;
  patientName: string;
  unitsBy: string;
  unitFor: string;
  unitsTo: string;
  units: number;
  creation_ts: string;
  units_family: string;
  units_family_label: string;
  units_work: string;
};

type LedgerList = {
  page: number;
  totalPages: number;
  total: number;
  fromDate: string;
  toDate: string;
};

export type LabDeliveryUnitsRow = {
  lab_token: string;
  lab_name: string;
  organisation: string;
  live_cases: number;
  live_units: number;
  redesign_cases: number;
  redesign_units: number;
  total_units: number;
};

export type LabDeliveryUnitsCaseRow = {
  case_id: string;
  type: string;
  lab_token: string;
  lab_name: string;
  created_at: string;
  delivery_units: number;
  units_by: string;
};

export type LabDeliveryUnitsExport = {
  fromDate: string;
  toDate: string;
  totals: {
    labs: number;
    live_cases: number;
    live_units: number;
    redesign_cases: number;
    redesign_units: number;
    total_units: number;
  };
  labs: LabDeliveryUnitsRow[];
  cases: LabDeliveryUnitsCaseRow[];
};

type UnitsLedgerState = LedgerList & {
  employees: UnitsLedgerEmployee[];
  logs: UnitsLedgerLog[];
  employeesLoading: boolean;
  logsLoading: boolean;
  exporting: boolean;
  saving: boolean;
  error: string | null;
};

const initialState: UnitsLedgerState = {
  employees: [],
  logs: [],
  page: 1,
  totalPages: 0,
  total: 0,
  fromDate: "",
  toDate: "",
  employeesLoading: false,
  logsLoading: false,
  exporting: false,
  saving: false,
  error: null,
};

type RangeQuery = {
  page?: number;
  limit?: number;
  search?: string;
  fromDate?: string;
  toDate?: string;
  empName?: string;
  family?: string;
  unitFor?: string;
};

export const fetchUnitsLedgerEmployees = createAsyncThunk(
  "unitsLedger/employees",
  async (params: RangeQuery, { rejectWithValue }) => {
    try {
      const res = await api.get("/units/ledger/employees", { params });
      return res.data.data as LedgerList & { employees: UnitsLedgerEmployee[] };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to load employee units",
        );
      }
      return rejectWithValue("Failed to load employee units");
    }
  },
);

export const fetchUnitsLedgerLogs = createAsyncThunk(
  "unitsLedger/logs",
  async (params: RangeQuery, { rejectWithValue }) => {
    try {
      const res = await api.get("/units/ledger/logs", { params });
      return res.data.data as LedgerList & { logs: UnitsLedgerLog[] };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to load unit logs",
        );
      }
      return rejectWithValue("Failed to load unit logs");
    }
  },
);

export const updateUnitsLedgerLog = createAsyncThunk(
  "unitsLedger/updateLog",
  async (
    payload: {
      id: number;
      units?: number;
      unitsTo?: string;
      unitFor?: string;
      units_By?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.put(`/units/ledger/logs/${payload.id}`, payload);
      return res.data.data as UnitsLedgerLog;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to update unit log",
        );
      }
      return rejectWithValue("Failed to update unit log");
    }
  },
);

export const exportLabDeliveryUnits = createAsyncThunk(
  "unitsLedger/labExport",
  async (
    params: { fromDate: string; toDate: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/units/ledger/lab-export", {
        params,
        timeout: 120000,
      });
      return res.data.data as LabDeliveryUnitsExport;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to export lab units",
        );
      }
      return rejectWithValue("Failed to export lab units");
    }
  },
);

export const createUnitsLedgerLog = createAsyncThunk(
  "unitsLedger/createLog",
  async (
    payload: {
      case_id: string;
      unitsTo: string;
      unitFor: string;
      units: number;
      units_By?: string;
      patientName?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post("/units/ledger/logs", payload);
      return res.data.data as UnitsLedgerLog;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to add unit credit",
        );
      }
      return rejectWithValue("Failed to add unit credit");
    }
  },
);

const unitsLedgerSlice = createSlice({
  name: "unitsLedger",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnitsLedgerEmployees.pending, (state) => {
        state.employeesLoading = true;
        state.error = null;
      })
      .addCase(fetchUnitsLedgerEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.employees = action.payload.employees;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.total = action.payload.total;
        state.fromDate = action.payload.fromDate;
        state.toDate = action.payload.toDate;
      })
      .addCase(fetchUnitsLedgerEmployees.rejected, (state, action) => {
        state.employeesLoading = false;
        state.error = (action.payload as string) || "Failed to load employees";
      })
      .addCase(fetchUnitsLedgerLogs.pending, (state) => {
        state.logsLoading = true;
        state.error = null;
      })
      .addCase(fetchUnitsLedgerLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.logs = action.payload.logs;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.total = action.payload.total;
        state.fromDate = action.payload.fromDate;
        state.toDate = action.payload.toDate;
      })
      .addCase(fetchUnitsLedgerLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = (action.payload as string) || "Failed to load logs";
      })
      .addCase(updateUnitsLedgerLog.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateUnitsLedgerLog.fulfilled, (state, action) => {
        state.saving = false;
        state.logs = state.logs.map((log) =>
          log.id === action.payload.id ? action.payload : log,
        );
      })
      .addCase(updateUnitsLedgerLog.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to update";
      })
      .addCase(exportLabDeliveryUnits.pending, (state) => {
        state.exporting = true;
        state.error = null;
      })
      .addCase(exportLabDeliveryUnits.fulfilled, (state) => {
        state.exporting = false;
      })
      .addCase(exportLabDeliveryUnits.rejected, (state, action) => {
        state.exporting = false;
        state.error = (action.payload as string) || "Failed to export lab units";
      })
      .addCase(createUnitsLedgerLog.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createUnitsLedgerLog.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createUnitsLedgerLog.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to add credit";
      });
  },
});

export default unitsLedgerSlice.reducer;
